import {
    action,
    comparer,
    computed,
    makeObservable,
    observable,
    reaction,
    runInAction,
} from "mobx";
import { BaseStore } from "stores/BaseStore";
import type { IRootStore } from "stores/RootStore";
import { AcxStore } from "stores/RootStore";
import { AuthStatus, AuthStore } from "stores/AuthStore";
import {
    isKboUserAgreementAcceptRejected,
    KboUserAgreementService,
} from "services/KboUserAgreementService";
import type {
    KboUserAgreementDocument,
    KboUserAgreementStatus,
} from "../Models/KboUserAgreement";

export type KboUserAgreementSaveState = "idle" | "saving" | "failed";

/*
 * The KBo User Agreement some users must accept before using KBo chat. Until
 * they do, AgentStore locks the chat. The server enforces the same rule, so this
 * only decides what the UI shows.
 */
@AcxStore
export default class KboUserAgreementStore extends BaseStore {
    private readonly authStore: AuthStore;
    private readonly service = new KboUserAgreementService();
    // Drops a status that arrives after the user or organization changed, or
    // after an acceptance it predates.
    private statusRequest = 0;
    // Bumped when the user or organization changes, so an acceptance still in
    // flight from before can't unlock the new one.
    private scope = 0;

    @observable.ref status?: KboUserAgreementStatus;
    @observable saveState: KboUserAgreementSaveState = "idle";
    /** "Not now" folds the agreement into a banner until they choose to review it. */
    @observable isCollapsed: boolean = false;
    /** Bumped to bring the agreement into view. */
    @observable scrollRequest: number = 0;
    /**
     * Reading progress lives here rather than in the card, so it survives "Not now",
     * a failed save and moving between the KBo panel and fullscreen. It is kept per
     * revision: changed text has to be read again, even under the same version.
     */
    @observable private readRevision?: string;
    private scrollTopByRevision = new Map<string, number>();

    constructor(public rootStore: IRootStore) {
        super("KboUserAgreementStore");
        makeObservable(this);

        this.authStore = rootStore.getStore(AuthStore);

        reaction(
            () => ({
                status: this.authStore.status,
                userId: this.authStore.effectiveUserId,
                organizationId:
                    this.authStore.orgStore?.selectedOrganization?.id,
            }),
            (scope) => {
                if (scope.status !== AuthStatus.Initialized) return;
                this.reset();
                void this.refresh();
            },
            { fireImmediately: true, equals: comparer.structural },
        );
    }

    @computed get requiresAcceptance(): boolean {
        return this.status?.requiresAcceptance === true;
    }

    @computed get canAccept(): boolean {
        return this.status?.canAccept === true;
    }

    @computed get agreement(): KboUserAgreementDocument | undefined {
        return this.status?.agreement ?? undefined;
    }

    @computed get hasReadAgreement(): boolean {
        return (
            !!this.agreement && this.readRevision === this.agreement.revision
        );
    }

    get savedScrollTop(): number {
        return this.agreement
            ? this.scrollTopByRevision.get(this.agreement.revision) ?? 0
            : 0;
    }

    public async refresh() {
        const request = ++this.statusRequest;
        try {
            const status = await this.service.getStatus();
            if (request !== this.statusRequest) return;
            this.setStatus(status);
        } catch (error) {
            if (request !== this.statusRequest) return;
            // Leave KBo usable; the server still refuses chat requests if the
            // agreement is required.
            console.error("Failed to load the KBo User Agreement status:", error);
        }
    }

    /** Resolves true once the acceptance is saved. */
    public async accept(): Promise<boolean> {
        const agreement = this.agreement;
        if (!agreement || !this.canAccept || this.saveState === "saving")
            return false;

        const scope = this.scope;
        this.setSaveState("saving");
        try {
            const status = await this.service.accept(agreement);
            if (scope !== this.scope) return false;
            runInAction(() => {
                this.statusRequest += 1;
                this.setStatus(status);
                this.saveState = "idle";
                this.isCollapsed = false;
            });
            return true;
        } catch (error) {
            if (scope !== this.scope) return false;

            if (isKboUserAgreementAcceptRejected(error)) {
                // Not a failure to save: the status changed, e.g. the text they read
                // was replaced. Show what applies now.
                await this.refresh();
                this.setSaveState("idle");
                return false;
            }

            this.setSaveState("failed");
            return false;
        }
    }

    @action markAgreementRead() {
        if (this.agreement) this.readRevision = this.agreement.revision;
    }

    public rememberScrollTop(scrollTop: number) {
        if (this.agreement)
            this.scrollTopByRevision.set(this.agreement.revision, scrollTop);
    }

    @action collapse() {
        this.isCollapsed = true;
    }

    @action expand() {
        this.isCollapsed = false;
        this.scrollRequest += 1;
    }

    @action private setStatus(status: KboUserAgreementStatus) {
        this.status = status;
    }

    @action private setSaveState(saveState: KboUserAgreementSaveState) {
        this.saveState = saveState;
    }

    @action private reset() {
        this.scope += 1;
        this.statusRequest += 1;
        this.status = undefined;
        this.saveState = "idle";
        this.isCollapsed = false;
        this.readRevision = undefined;
        this.scrollTopByRevision.clear();
    }
}
